import {
  apiErrorSchema,
  draftSavedSchema,
  isSectionUnavailable,
  portalPacketSchema,
  reissueAcceptedSchema,
  reissueByEmailRequestSchema,
  reissueFromTokenRequestSchema,
  uploadIntentSchema,
  uploadedFileSchema,
  type PortalPacket,
  type PortalStep,
  type UploadedFile,
} from '@vigeo/shared';

/**
 * The portal's own API client.
 *
 * Deliberately separate from `lib/api.ts`: that one is the HR surface and knows how to ask
 * for people, checklists and catalog config. Nothing here may reach any of it — a candidate
 * has no business having a client capable of listing people, even one they cannot
 * successfully call.
 *
 * Every response is `no-store` server-side; `credentials: 'same-origin'` carries the
 * session cookie, which is `Path=/api/portal` so it never reaches the HR routes.
 */

export const packetSchema = portalPacketSchema;
export type Packet = PortalPacket;
export type PacketStep = PortalStep;

/** What the API says went wrong, in the words it chose. Never reworded here. */
export interface PortalError {
  readonly status: number;
  readonly message: string;
  readonly canReissue: boolean;
  /** The server was built with the portal closed (#625). Nothing the person does here can change that. */
  readonly unavailable?: true;
}


async function call(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
}

async function toError(response: Response): Promise<PortalError> {
  const json: unknown = await response.json().catch(() => ({}));
  if (isSectionUnavailable(json)) return { status: response.status, message: json.error, canReissue: false, unavailable: true };
  const parsed = apiErrorSchema.safeParse(json);
  // A fresh link is offered unless the API said not to — a refusal that names no reason is a session problem, not a link problem.
  const body = parsed.success ? parsed.data : { error: 'Something went wrong.', canReissue: true, issues: [] };
  return { status: response.status, message: body.error, canReissue: body.canReissue ?? true };
}

/**
 * Exchange a link and a code for a session.
 *
 * Both go in the BODY. A token in a query string ends up in the address bar, in browser
 * history, in a screenshot the candidate sends a friend, and in Application Insights.
 */
export async function openSession(token: string, accessCode: string): Promise<PortalError | null> {
  const response = await call('/api/portal/session', {
    method: 'POST',
    body: JSON.stringify({ token, accessCode }),
  });
  return response.ok ? null : toError(response);
}

/**
 * Ask for a fresh link (#82). The API answers every request the same way, so there is nothing
 * to branch on: the message it returns is shown as it is. Null means the request never got an
 * answer at all — a dropped connection — and only that is worth a "try again".
 *
 * With a token, it is the link the person arrived with. Without one, the session cookie is the
 * proof — the mid-form case, where the link was stripped from the address bar long ago.
 */
export async function requestReissueFromToken(token: string | null): Promise<string | null> {
  const body = reissueFromTokenRequestSchema.parse(token === null ? {} : { token });
  return reissueCall('/api/portal/reissue-from-token', body);
}

/** The fallback for someone with no link at all. The link goes to the address on file, never this one. */
export async function requestReissueByEmail(email: string): Promise<string | null> {
  const parsed = reissueByEmailRequestSchema.safeParse({ email });
  // A typo is not sent: the API would answer the same as for any address, and the person would
  // wait for an email that cannot come. This check is about their typing, not about us.
  if (!parsed.success) return null;
  return reissueCall('/api/portal/reissue', parsed.data);
}

async function reissueCall(path: string, body: unknown): Promise<string | null> {
  try {
    const response = await call(path, { method: 'POST', body: JSON.stringify(body) });
    const parsed = reissueAcceptedSchema.safeParse(await response.json().catch(() => null));
    return parsed.success ? parsed.data.message : null;
  } catch {
    return null;
  }
}

export async function fetchPacket(): Promise<Packet | PortalError> {
  const response = await call('/api/portal/packet');
  if (!response.ok) return toError(response);
  return packetSchema.parse(await response.json());
}

export interface DraftSaved {
  readonly revision: number;
  readonly savedAt: string;
}

export async function saveDraft(
  instanceId: string,
  value: unknown,
  revision: number,
): Promise<DraftSaved | PortalError> {
  const response = await call(`/api/portal/steps/${encodeURIComponent(instanceId)}/draft`, {
    method: 'PUT',
    body: JSON.stringify({ value, revision }),
  });
  if (!response.ok) return toError(response);
  return draftSavedSchema.parse(await response.json());
}

export function isPortalError(value: unknown): value is PortalError {
  return typeof value === 'object' && value !== null && 'status' in value && 'message' in value;
}

/* -----------------------------------------------------------------------------------
 * Uploads.
 * -------------------------------------------------------------------------------- */

export type { UploadedFile } from '@vigeo/shared';

/**
 * Reserve, PUT, confirm.
 *
 * The bytes go straight to storage under the short-lived SAS the API mints; the API sees
 * only the control calls. Putting a 20 MB phone upload through a Consumption-plan function
 * would mean paying for the whole transfer and racing a 230-second timeout that is not
 * theoretical on the connections this audience actually has.
 *
 * `onProgress` gets a 0..1 fraction. XHR rather than fetch because fetch still cannot
 * report upload progress, and a progress bar is the difference between waiting and
 * assuming it has frozen.
 */
export async function uploadFile(
  instanceId: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<UploadedFile | PortalError> {
  const step = encodeURIComponent(instanceId);

  const reserved = await call(`/api/portal/steps/${step}/upload-intent`, {
    method: 'POST',
    body: JSON.stringify({ contentType: file.type, byteLength: file.size, fileName: file.name }),
  });
  if (!reserved.ok) return toError(reserved);
  const intent = uploadIntentSchema.parse(await reserved.json());

  const written = await putBytes(intent.uploadUrl, file, onProgress);
  if (!written) {
    // Deliberately not the storage account's own error. It is XML, it names the account,
    // and it is meaningless to the person reading it.
    return { status: 502, message: 'The upload did not finish. Please try again.', canReissue: false };
  }

  const confirmed = await call(`/api/portal/steps/${step}/upload-complete`, {
    method: 'POST',
    body: JSON.stringify({ uploadId: intent.uploadId }),
  });
  if (!confirmed.ok) return toError(confirmed);
  return uploadedFileSchema.parse(await confirmed.json());
}

function putBytes(url: string, file: File, onProgress: (fraction: number) => void): Promise<boolean> {
  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url, true);
    // Required by the Blob REST API for a single-shot PUT; it is also one of the headers
    // the account's CORS rule allows, and the upload fails at preflight without it.
    request.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    request.setRequestHeader('content-type', file.type);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () => resolve(request.status >= 200 && request.status < 300);
    request.onerror = () => resolve(false);
    request.onabort = () => resolve(false);
    request.send(file);
  });
}

/* -----------------------------------------------------------------------------------
 * Submit.
 * -------------------------------------------------------------------------------- */

export interface SubmitFailure extends PortalError {
  readonly issues: readonly string[];
}

export async function submitStep(instanceId: string, submission: unknown): Promise<null | PortalError> {
  const response = await call(`/api/portal/steps/${encodeURIComponent(instanceId)}/submit`, {
    method: 'POST',
    body: JSON.stringify(submission),
  });
  if (response.ok) return null;

  const body: unknown = await response.json().catch(() => ({}));
  const detailed = apiErrorSchema.safeParse(body);
  if (detailed.success && detailed.data.issues.length > 0) {
    return {
      status: response.status,
      message: `${detailed.data.error} ${detailed.data.issues.join(' ')}`.trim(),
      canReissue: false,
    };
  }
  return toError(response);
}

/** Seal the packet once nothing in it is open. A 409 means something still is. */
export async function snapshotPacket(): Promise<boolean> {
  const response = await call('/api/portal/packet/snapshot', { method: 'POST' });
  return response.ok;
}

/**
 * Fetch a pinned template's bytes through the portal session.
 *
 * Same cookie as everything else here, so the document is exactly as protected as the packet
 * that referenced it, and the URL comes from the packet rather than being built by the
 * client — there is no parameter for a person to edit toward somebody else's form.
 */
export async function loadTemplateBytes(bytesUrl: string): Promise<Uint8Array> {
  const response = await call(bytesUrl, { headers: {} });
  if (!response.ok) throw new Error('template unavailable');
  return new Uint8Array(await response.arrayBuffer());
}
